import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import StaticRouteModal from '../../../components/edges/StaticRouteModal';
import edgeService from '../../../services/edge.service';
import { useTenantStore } from '../../../stores/tenantStore';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

interface StaticRoutesProps {
  edgeId: string;
}

const StaticRoutes = ({ edgeId }: StaticRoutesProps) => {
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<any>(null);
  const [routes, setRoutes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ routeId: string; destination: string } | null>(null);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        setLoading(true);
        const response = await edgeService.listStaticRoutes(edgeId);
        setRoutes(response.items || []);
      } catch (err) {
        console.error('Failed to load static routes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [edgeId, selectedTenantId]);

  const handleAdd = () => {
    setIsAddModalOpen(true);
  };

  const handleEdit = (route: any) => {
    setEditingRoute(route);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await edgeService.deleteStaticRoute(edgeId, deleteConfirm.routeId);
      setDeleteConfirm(null);
      // Refresh routes
      const response = await edgeService.listStaticRoutes(edgeId);
      setRoutes(response.items || []);
    } catch (err: any) {
      toast.error('Delete Failed', `Failed to delete route: ${err.message}`);
    }
  };

  const handleSave = async (data: any) => {
    try {
      if (editingRoute) {
        await edgeService.updateStaticRoute(edgeId, editingRoute.id, data);
      } else {
        await edgeService.createStaticRoute(edgeId, data);
      }
      setIsAddModalOpen(false);
      setEditingRoute(null);
      // Refresh routes
      const response = await edgeService.listStaticRoutes(edgeId);
      setRoutes(response.items || []);
    } catch (err: any) {
      toast.error('Save Failed', `Failed to save route: ${err.message}`);
    }
  };

  // Format destination with CIDR notation
  const formatDestination = (route: any): string => {
    const ip = route.target_ip || route.destination || '';
    const mask = route.ip_mask || route.subnetMask || '';

    // If IP already has CIDR, return as-is
    if (ip.includes('/')) return ip;

    // If we have a mask, append it
    if (mask) {
      if (mask.startsWith('/')) {
        return `${ip}${mask}`;
      }
      // Convert subnet mask to CIDR
      const cidrMap: Record<string, string> = {
        '255.255.255.255': '/32',
        '255.255.255.252': '/30',
        '255.255.255.0': '/24',
        '255.255.252.0': '/22',
        '255.255.0.0': '/16',
        '255.0.0.0': '/8',
        '0.0.0.0': '/0',
      };
      const cidr = cidrMap[mask];
      if (cidr) return `${ip}${cidr}`;
    }

    return ip || '-';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Static Routes</h3>
          <p className="text-sm text-gray-600 mt-1">Configure static routing table entries</p>
        </div>
        {canCreate && (
          <button onClick={handleAdd} className="btn-primary inline-flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Static Route</span>
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Destination (CIDR)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interface</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Loading routes...
                </td>
              </tr>
            ) : routes.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No static routes found
                </td>
              </tr>
            ) : (
              routes.map((route) => (
                <tr key={route.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {formatDestination(route)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {route.next_hop_ip || route.gateway || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{route.inter_name || route.interface || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{route.metric || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{route.description || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    {canUpdate && (
                      <button
                        onClick={() => handleEdit(route)}
                        className="inline-flex items-center text-primary-600 hover:text-primary-800 font-medium"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => setDeleteConfirm({ routeId: route.id, destination: route.target_ip || route.destination || 'Unknown' })}
                        className="inline-flex items-center text-red-600 hover:text-red-800 font-medium"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      <StaticRouteModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSave}
        mode="add"
        edgeId={edgeId}
      />
      <StaticRouteModal
        isOpen={!!editingRoute}
        onClose={() => setEditingRoute(null)}
        onSave={handleSave}
        route={editingRoute}
        mode="edit"
        edgeId={edgeId}
      />
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Static Route"
        message={`Are you sure you want to delete the route to ${deleteConfirm?.destination}?`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default StaticRoutes;
